import { useEffect, useMemo, useRef, useState } from 'react';

import ResultPopup from '@components/General/ResultPopup';
import { usePopup } from '@contexts/popupContext';
import { Application } from '@core/Application';
import ClickableWrapper from '@shared/designSystem/ClickableWrapper';
import { HollowedButton, NormalButton } from '@shared/designSystem/Button';
import { ColumnsContainer, HorizontalContainer, SimpleCardContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { ComboBox, ComboBoxOption } from '@shared/designSystem/ComboBox';
import DetailField from '@shared/designSystem/DetailField';
import { Loader } from '@shared/designSystem/Loader';
import { SystemIcon } from '@shared/designSystem/SystemIcon';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import { TextInput, TextInputRef } from '@shared/designSystem/TextInput';
import { BoldText, SmallText, Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { CollaboratorCustomField } from '@services/CollaboratorCustomFieldsService';
import { UsersService } from '@services/UsersService';
import { QSwitch } from '@shared/designSystem/QSwitch';

interface ColaboradorInfoProps {
    orgID: string;
    colaborador: any;
    setColaborador: (colaborador: any) => void | Promise<void>;
    startInEditMode?: boolean;
    customFields?: CollaboratorCustomField[];
    hideTitle?: boolean;
    onSaveSuccess?: () => void | Promise<void>;
    hideEditButton?: boolean;
    hideCancelButton?: boolean;
    readOnly?: boolean;
}

export default function ColaboradorInfo({
    orgID,
    colaborador,
    setColaborador,
    startInEditMode = false,
    customFields = [],
    hideTitle = false,
    onSaveSuccess,
    hideEditButton = false,
    hideCancelButton = false,
    readOnly = false,
}: ColaboradorInfoProps) {
    const { showPopup } = usePopup();
    const usersService = useMemo(
        () => new UsersService(Application.shared().coreDependencies.httpClient),
        [],
    );

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const nameInputRef = useRef<TextInputRef>(null);
    const savingRef = useRef(false);

    const [isEditing, setIsEditing] = useState(readOnly ? false : startInEditMode);
    const [loadingPhoto, setLoadingPhoto] = useState(false);
    const [saving, setSaving] = useState(false);
    const [imgUrl, setImgUrl] = useState('');
    const [isConfirmIdentificationVisible, setIsConfirmIdentificationVisible] = useState(false);
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    const [roster, setRoster] = useState<Array<{ id: string; name: string; mobilePhone: string; deactivated: boolean }>>([]);
    const [draft, setDraft] = useState({
        name: '',
        dni: '',
        mobilePhone: '',
        email: '',
        empresa: '',
        address: '',
        localidad: '',
        legajo: '',
        birthDate: '',
        jobTitle: '',
    });
    const [errors, setErrors] = useState({
        name: '',
        dni: '',
        mobilePhone: '',
        email: '',
        empresa: '',
        address: '',
        localidad: '',
        legajo: '',
        birthDate: '',
        jobTitle: '',
    });
    const profileSidebarWidth = '220px';
    const profileAvatarSize = '148px';
    const profileSidebarStyle = {
        width: profileSidebarWidth,
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
    };
    const profileSidebarSpacerStyle = {
        width: profileSidebarWidth,
        flexShrink: 0,
    };

    useEffect(() => {
        if (!colaborador) {
            return;
        }

        setDraft({
            name: colaborador.name || '',
            dni: colaborador.dni || '',
            mobilePhone: normalizePhoneForInput(colaborador.mobilePhone),
            email: colaborador.email || '',
            empresa: colaborador.empresa || '',
            address: colaborador.address || '',
            localidad: colaborador.localidad || '',
            legajo: colaborador.legajo || '',
            birthDate: formatDateOnlyForDisplay(colaborador.birthDate),
            jobTitle: colaborador.jobTitle || '',
        });
        setImgUrl(colaborador.photo || '');
    }, [colaborador]);

    useEffect(() => {
        setCustomFieldValues(buildCustomFieldFormValues(customFields, colaborador?.customFields || {}));
    }, [colaborador?.customFields, customFields]);

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        let cancelled = false;
        usersService.getNomina(orgID)
            .then((rows: any[]) => {
                if (cancelled) {
                    return;
                }
                setRoster((rows || [])
                    .filter((row) => row?.id)
                    .map((row) => ({
                        id: row.id,
                        name: row.name ?? '',
                        mobilePhone: row.mobilePhone ?? '',
                        deactivated: Boolean(row.deactivationTimestamp),
                    })));
            })
            .catch((error) => {
                console.error(error);
            });

        return () => {
            cancelled = true;
        };
    }, [isEditing, orgID, usersService]);

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        const timeout = window.setTimeout(() => {
            nameInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [isEditing]);

    const initials = buildInitials(draft.name || colaborador?.name || '');
    const viewFields = [
        { key: 'name', label: 'Nombre y Apellido', value: colaborador?.name },
        { key: 'mobilePhone', label: 'Teléfono', value: formatPhoneForDisplay(colaborador?.mobilePhone) },
        { key: 'email', label: 'Email', value: colaborador?.email },
        { key: 'dni', label: 'Identificación', value: colaborador?.dni },
        { key: 'birthDate', label: 'Fecha de nacimiento', value: formatDateOnlyForDisplay(colaborador?.birthDate) },
        { key: 'legajo', label: 'Legajo', value: colaborador?.legajo },
        { key: 'empresa', label: 'Empresa', value: colaborador?.empresa },
        { key: 'jobTitle', label: 'Puesto', value: colaborador?.jobTitle },
        { key: 'address', label: 'Domicilio', value: colaborador?.address },
        { key: 'localidad', label: 'Localidad', value: colaborador?.localidad },
    ];

    const editFields: Array<{
        key: keyof typeof draft;
        label: string;
        placeholder: string;
        type: 'text' | 'tel' | 'email';
        sanitize?: (value: string) => string;
    }> = [
        {
            key: 'name',
            label: 'Nombre y Apellido',
            placeholder: 'Nombre y Apellido',
            type: 'text' as const,
            sanitize: (value: string) => value.replace(/[^a-zA-Z\u00C0-\u00FF ]/g, ''),
        },
        {
            key: 'mobilePhone',
            label: 'Teléfono',
            placeholder: '+5491112345678',
            type: 'tel' as const,
            sanitize: sanitizePhoneInput,
        },
        {
            key: 'email',
            label: 'Email',
            placeholder: 'Ingresar email',
            type: 'email' as const,
        },
        {
            key: 'dni',
            label: 'Identificación',
            placeholder: 'Ingresar identificación',
            type: 'text' as const,
            sanitize: (value: string) => value.replace(/[^0-9-]/g, ''),
        },
        {
            key: 'birthDate',
            label: 'Fecha de nacimiento',
            placeholder: 'DD/MM/AAAA',
            type: 'text' as const,
            sanitize: sanitizeDateOnlyDisplayInput,
        },
        {
            key: 'legajo',
            label: 'Legajo',
            placeholder: 'Ingresar Nro. Legajo',
            type: 'text' as const,
        },
        {
            key: 'empresa',
            label: 'Empresa',
            placeholder: 'Ingresar empresa',
            type: 'text' as const,
        },
        {
            key: 'jobTitle',
            label: 'Puesto',
            placeholder: 'Ingresar puesto',
            type: 'text' as const,
        },
        {
            key: 'address',
            label: 'Domicilio',
            placeholder: 'Ingresar domicilio',
            type: 'text' as const,
        },
        {
            key: 'localidad',
            label: 'Localidad',
            placeholder: 'Ingresar localidad',
            type: 'text' as const,
        },
    ];

    const handleDraftChange = (field: keyof typeof draft, value: string) => {
        setDraft((currentDraft) => ({ ...currentDraft, [field]: value }));
        setErrors((currentErrors) => ({ ...currentErrors, [field]: '' }));
    };

    const handleCustomFieldChange = (fieldID: string, value: string) => {
        setCustomFieldValues((currentValues) => ({ ...currentValues, [fieldID]: value }));
        setCustomFieldErrors((currentErrors) => ({ ...currentErrors, [fieldID]: '' }));
    };

    const renderCustomFieldInput = (field: CollaboratorCustomField) => {
        const value = customFieldValues[field.id] ?? '';
        const label = buildCustomFieldLabel(field);
        const error = customFieldErrors[field.id];

        if (field.typeId === 'boolean') {
            const checked = value === 'true';

            return (
                <VerticalContainer key={field.id} width="100%" gap={designSystemStyles.gapXxxSm}>
                    <SmallText text={label} />
                    <div
                        style={{
                            width: '100%',
                            minHeight: '50px',
                            border: `1px solid ${error ? designSystemStyles.colorError : designSystemStyles.colorTextInputBorder}`,
                            borderRadius: designSystemStyles.cornerRadiusComponent,
                            boxShadow: designSystemStyles.shadowTextInput,
                            backgroundColor: designSystemStyles.colorBackgroundWhite,
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 12px',
                            gap: '12px',
                        }}
                    >
                        <span
                            style={{
                                color: designSystemStyles.colorText,
                                fontFamily: designSystemStyles.fontFamily,
                                fontSize: designSystemStyles.fontSizeSm,
                                fontWeight: designSystemStyles.fontWeightText,
                                lineHeight: 'normal',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {checked ? "Sí".localized() : "No".localized()}
                        </span>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <QSwitch
                                checked={checked}
                                onChange={() => handleCustomFieldChange(field.id, checked ? 'false' : 'true')}
                            />
                        </div>
                    </div>
                    {error ? (
                        <SmallText text={error} color={designSystemStyles.colorError} />
                    ) : null}
                </VerticalContainer>
            );
        }

        if (field.typeId === 'list') {
            const options = buildListFieldOptions(field);
            const selectedOption = options.find((option) => option.id === value);

            return (
                <VerticalContainer key={field.id} width="100%" gap={designSystemStyles.gapXxxSm}>
                    <SmallText text={label} />
                    <ComboBox
                        options={field.isRequired ? options : [{ id: '', title: 'Sin seleccionar'.localized() }, ...options]}
                        value={selectedOption}
                        placeholder={"Seleccionar".localized()}
                        iconType={null as never}
                        disabled={saving}
                        onChange={(option) => handleCustomFieldChange(field.id, option.id)}
                    />
                    {error ? (
                        <SmallText text={error} color={designSystemStyles.colorError} />
                    ) : null}
                </VerticalContainer>
            );
        }

        if (field.typeId === 'number') {
            return (
                <TextInput
                    key={field.id}
                    label={label}
                    value={value}
                    placeholder={field.name}
                    type="number"
                    inputMode="decimal"
                    error={error}
                    onChange={(nextValue) => handleCustomFieldChange(field.id, sanitizeNumberInput(nextValue))}
                    onEnterPress={handleEnterPress}
                />
            );
        }

        if (field.typeId === 'date') {
            return (
                <TextInput
                    key={field.id}
                    label={label}
                    value={value}
                    placeholder={"DD/MM/AAAA".localized()}
                    inputMode="numeric"
                    error={error}
                    onChange={(nextValue) => handleCustomFieldChange(field.id, sanitizeDateOnlyDisplayInput(nextValue))}
                    onEnterPress={handleEnterPress}
                />
            );
        }

        return (
            <TextInput
                key={field.id}
                label={label}
                value={value}
                placeholder={field.name}
                error={error}
                onChange={(nextValue) => handleCustomFieldChange(field.id, nextValue)}
                onEnterPress={handleEnterPress}
            />
        );
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsConfirmIdentificationVisible(false);
        setCustomFieldErrors({});
        setErrors({
            name: '',
            dni: '',
            mobilePhone: '',
            email: '',
            empresa: '',
            address: '',
            localidad: '',
            legajo: '',
            birthDate: '',
            jobTitle: '',
        });
        setDraft({
            name: colaborador?.name || '',
            dni: colaborador?.dni || '',
            mobilePhone: normalizePhoneForInput(colaborador?.mobilePhone),
            email: colaborador?.email || '',
            empresa: colaborador?.empresa || '',
            address: colaborador?.address || '',
            localidad: colaborador?.localidad || '',
            legajo: colaborador?.legajo || '',
            birthDate: formatDateOnlyForDisplay(colaborador?.birthDate),
            jobTitle: colaborador?.jobTitle || '',
        });
        setCustomFieldValues(buildCustomFieldFormValues(customFields, colaborador?.customFields || {}));
    };

    const handleSave = async (identificationConfirmed: boolean = false) => {
        if (savingRef.current) {
            return;
        }

        const nextErrors = validateDraft(draft, roster, colaborador?.id);
        const nextCustomFieldErrors = validateCustomFields(customFields, customFieldValues);

        setErrors(nextErrors);
        setCustomFieldErrors(nextCustomFieldErrors);

        if (
            Object.values(nextErrors).some(Boolean) ||
            Object.values(nextCustomFieldErrors).some(Boolean)
        ) {
            return;
        }

        const payload = {
            name: draft.name.trim(),
            dni: draft.dni,
            mobilePhone: `whatsapp:+${draft.mobilePhone.replace(/\D/g, '')}`,
            email: draft.email.trim(),
            empresa: draft.empresa,
            address: draft.address,
            localidad: draft.localidad,
            legajo: draft.legajo,
            birthDate: parseDateOnlyFromDisplay(draft.birthDate),
            jobTitle: draft.jobTitle.trim(),
            customFields: buildCustomFieldsPayload(customFields, customFieldValues),
            expirationDate: 4133991599000,
        };

        if (!hasCollaboratorChanges(colaborador, payload)) {
            setIsEditing(false);
            ToastManager.shared().showInfoMessage('No se realizaron cambios'.localized());
            await onSaveSuccess?.();
            return;
        }

        // Only send the fields that actually changed. Sending untouched fields
        // (even with their current value) makes the backend register them as
        // "edited" in the audit trail, so we build a minimal diff instead of
        // always resending the full profile.
        const changedFields = buildChangedFields(colaborador, payload);

        // If this collaborator already has an identification (dni/CUIL) linked to
        // pay slips, and we're about to change their name or phone WITHOUT
        // touching the dni, double-check with whoever is editing: shared/corporate
        // phones sometimes get handed off to a different person, and if the dni
        // isn't updated along with the name, pay slips could keep going out under
        // the wrong identity. Skip the extra step if the dni itself is changing,
        // since that's already an explicit review of the identification, or if
        // this call is already the confirmed retry.
        const nameChanged = normalizeComparableText(colaborador?.name) !== normalizeComparableText(payload.name);
        const phoneChanged = normalizePhoneForInput(colaborador?.mobilePhone) !== normalizePhoneForInput(payload.mobilePhone);
        const dniChanged = normalizeComparableText(colaborador?.dni) !== normalizeComparableText(payload.dni);
        const hasExistingDni = normalizeComparableText(colaborador?.dni) !== '';
        const needsIdentificationConfirmation = hasExistingDni && !dniChanged && (nameChanged || phoneChanged) && !identificationConfirmed;

        if (needsIdentificationConfirmation) {
            setIsConfirmIdentificationVisible(true);
            return;
        }

        setIsConfirmIdentificationVisible(false);
        savingRef.current = true;
        setSaving(true);
        try {
            await usersService.editUser(orgID, colaborador.id, {
                ...changedFields,
                expirationDate: payload.expirationDate,
                ...(identificationConfirmed ? { identificationConfirmed: true } : {}),
            });

            await applyLocalCollaboratorState(payload);
            setIsEditing(false);
            ToastManager.shared().showSuccessMessage('Cambios guardados correctamente'.localized());
            await onSaveSuccess?.();
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al editar usuario..."} succesful={false} />);
            console.error(error);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const handleConfirmIdentificationAndSave = () => handleSave(true);

    const handleCancelIdentificationConfirmation = () => {
        setIsConfirmIdentificationVisible(false);
    };

    // Pressing Enter while the identification confirmation card is showing
    // should confirm it (same action as the "Confirmar y guardar" button),
    // not re-run the initial save check — which would just re-show the same
    // card and look like Enter does nothing.
    const handleEnterPress = () => {
        if (isConfirmIdentificationVisible) {
            if (!saving) handleConfirmIdentificationAndSave();
        } else {
            handleSave();
        }
    };

    const applyLocalCollaboratorState = async (payload: {
        name: string;
        dni: string;
        mobilePhone: string;
        email: string;
        empresa: string;
        address: string;
        localidad: string;
        legajo: string;
        birthDate: string;
        jobTitle: string;
        customFields: Record<string, string>;
    }) => {
        await setColaborador({
            ...colaborador,
            name: payload.name,
            dni: payload.dni,
            mobilePhone: payload.mobilePhone,
            email: payload.email,
            empresa: payload.empresa,
            address: payload.address,
            localidad: payload.localidad,
            legajo: payload.legajo,
            birthDate: payload.birthDate,
            jobTitle: payload.jobTitle,
            customFields: payload.customFields,
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) {
            return;
        }

        const formData = new FormData();
        formData.append('photo', selectedFile);

        setLoadingPhoto(true);
        try {
            const result = await usersService.uploadPhoto(colaborador.id, orgID, formData);
            setImgUrl(result.url);
            setColaborador({ ...colaborador, photo: result.url });
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al actualizar la foto..."} succesful={false} />);
            console.error(error);
        } finally {
            setLoadingPhoto(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
            {!hideTitle ? (
                <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="center">
                    <SystemIcon type="profile" color={designSystemStyles.colorTextSecondary} />
                    <BoldText text={'Datos Personales'} size="md" />
                </HorizontalContainer>
            ) : null}

            <div style={{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <VerticalContainer
                    width="100%"
                    gap={designSystemStyles.gapLg}
                    paddingBottom={designSystemStyles.paddingSm}
                >
                    <HorizontalContainer
                        width="100%"
                        alignment="flex-start"
                        gap={designSystemStyles.gapLg}
                    >
                        <div style={profileSidebarStyle}>
                            <VerticalContainer width={profileAvatarSize} gap={designSystemStyles.gapMd} alignment="center">
                                <div
                                    style={{
                                        position: 'relative',
                                        width: profileAvatarSize,
                                        height: profileAvatarSize,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'visible',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: profileAvatarSize,
                                            height: profileAvatarSize,
                                            borderRadius: '50%',
                                            backgroundColor: designSystemStyles.colorUserInitialsBackground,
                                            border: `1px solid ${designSystemStyles.colorUserInitialsBorder}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {imgUrl ? (
                                            <img
                                                src={imgUrl}
                                                alt={draft.name || colaborador?.name || 'Colaborador'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: 'center center',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : (
                                            <BoldText
                                                text={initials}
                                                size="lg"
                                                color={designSystemStyles.colorTitleText}
                                            />
                                        )}
                                    </div>
                                    {!readOnly ? (
                                        <ClickableWrapper
                                            onClick={() => fileInputRef.current?.click()}
                                            style={{
                                                position: 'absolute',
                                                bottom: '6px',
                                                right: '6px',
                                                zIndex: 2,
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                backgroundColor: 'white',
                                                boxShadow: '0 8px 24px rgba(16, 24, 40, 0.10)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            title={'Actualizar foto'}
                                        >
                                            <SystemIcon type="camera" color={designSystemStyles.colorTextSecondary} />
                                        </ClickableWrapper>
                                    ) : null}
                                    {loadingPhoto ? (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                zIndex: 3,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255, 255, 255, 0.72)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Loader height="48px" width="48px" />
                                        </div>
                                    ) : null}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                    />
                                </div>

                                <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm} alignment="center">
                                    <BoldText text={draft.name || colaborador?.name || 'Sin nombre'} size="md" />
                                </VerticalContainer>
                            </VerticalContainer>
                        </div>

                        <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                            {!isEditing ? (
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                    {viewFields.map((field) => (
                                        <DetailField
                                            key={field.key}
                                            label={field.label}
                                            value={field.value}
                                            emptyValue={''}
                                        />
                                    ))}
                                </ColumnsContainer>
                            ) : (
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                    {editFields.map((field) => (
                                        <TextInput
                                            key={field.key}
                                            ref={field.key === 'name' ? nameInputRef : undefined}
                                            label={field.label}
                                            value={draft[field.key]}
                                            placeholder={field.placeholder}
                                            type={field.type}
                                            inputMode={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : 'text'}
                                            error={errors[field.key]}
                                            loading={saving}
                                            onChange={(value) => handleDraftChange(field.key, field.sanitize ? field.sanitize(value) : value)}
                                            onEnterPress={handleEnterPress}
                                        />
                                    ))}
                                </ColumnsContainer>
                            )}
                        </VerticalContainer>
                    </HorizontalContainer>

                    {customFields.length > 0 ? (
                        <>
                            <HorizontalContainer
                                width="100%"
                                alignment="flex-start"
                                gap={designSystemStyles.gapLg}
                            >
                                <div style={profileSidebarSpacerStyle} />
                                <div
                                    style={{
                                        width: '100%',
                                        borderTop: `1px solid ${designSystemStyles.colorTextInputBorder}`,
                                    }}
                                />
                            </HorizontalContainer>

                            <HorizontalContainer
                                width="100%"
                                alignment="flex-start"
                                gap={designSystemStyles.gapLg}
                            >
                                <div style={profileSidebarSpacerStyle} />
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg} width="100%">
                                    {customFields.map((field) => (
                                        isEditing ? (
                                            renderCustomFieldInput(field)
                                        ) : (
                                            <DetailField
                                                key={field.id}
                                                label={buildCustomFieldLabel(field)}
                                                value={getCustomFieldDisplayValue(field, customFieldValues[field.id] ?? '')}
                                                emptyValue={''}
                                            />
                                        )
                                    ))}
                                </ColumnsContainer>
                            </HorizontalContainer>
                        </>
                    ) : null}
                </VerticalContainer>
            </div>

            <HorizontalContainer
                width="100%"
                alignment="flex-start"
                gap={designSystemStyles.gapLg}
                margin={`0 0 ${designSystemStyles.paddingSm} 0`}
            >
                <div style={profileSidebarSpacerStyle} />
                <VerticalContainer width="100%" gap={designSystemStyles.gapNone}>
                    {isEditing && isConfirmIdentificationVisible ? (
                        // Same inline setup-card pattern used by "Enviar a firmar" in
                        // SourceDocumentDetailsScreen (payslips) and mirrored in
                        // CollaboratorDetailsScreen there: instead of stacking a second
                        // popup on top of this screen, swap the action row for an inline
                        // card in place, within the same view.
                        <SimpleCardContainer width="100%" variant="inlineSetup">
                            <VerticalContainer gap={designSystemStyles.gapSm} alignment="start">
                                <Text
                                    text={'Este colaborador ya tiene una identificación asociada. Confirmá que sigue correspondiendo a este colaborador antes de guardar.'.localized()}
                                    color={designSystemStyles.colorTitleText}
                                />
                                <HorizontalContainer
                                    width="auto"
                                    justifyContent="center"
                                    padding={`${designSystemStyles.paddingMd} ${designSystemStyles.paddingLg}`}
                                    margin="0 auto"
                                >
                                    <BoldText
                                        text={colaborador?.dni ?? ''}
                                        size="md"
                                        alignment="center"
                                        color={designSystemStyles.colorWarningText}
                                    />
                                </HorizontalContainer>
                                <HorizontalContainer justifyContent="right" gap={designSystemStyles.gapSm} width="100%">
                                    <HollowedButton
                                        text={'Cancelar'}
                                        onClick={handleCancelIdentificationConfirmation}
                                        width="180px"
                                    />
                                    <NormalButton
                                        text={'Confirmar y guardar'}
                                        onClick={handleConfirmIdentificationAndSave}
                                        loading={saving}
                                        disabled={saving}
                                        width="180px"
                                    />
                                </HorizontalContainer>
                            </VerticalContainer>
                        </SimpleCardContainer>
                    ) : isEditing ? (
                        <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                            {!hideCancelButton ? (
                                <HollowedButton
                                    text={'Cancelar'}
                                    onClick={handleCancelEdit}
                                    disabled={saving}
                                    width="140px"
                                />
                            ) : null}
                            <NormalButton
                                text={'Guardar'}
                                onClick={() => handleSave()}
                                loading={saving}
                                width="140px"
                            />
                        </HorizontalContainer>
                    ) : hideEditButton || readOnly ? null : (
                        <ColumnsContainer columns={2} gap={designSystemStyles.gapLg} width="100%">
                            <div />
                            <HorizontalContainer justifyContent="flex-start" width="100%">
                                <HollowedButton
                                    text={'Editar'}
                                    onClick={() => setIsEditing(true)}
                                    width="140px"
                                />
                            </HorizontalContainer>
                        </ColumnsContainer>
                    )}
                </VerticalContainer>
            </HorizontalContainer>
        </VerticalContainer>
    );
}

const normalizePhoneForInput = (value: string | undefined) => {
    if (!value) {
        return '';
    }

    return value
        .replace(/^whatsapp:/i, '')
        .replace(/^\+/, '')
        .replace(/\s/g, '');
};

const normalizeComparableText = (value: unknown) => String(value ?? '').trim();

const sanitizePhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return value.startsWith('+') ? `+${digits}` : digits;
};

const normalizePhoneForComparison = (value: string | undefined) =>
    (value || '').replace(/^whatsapp:/i, '').replace(/\D/g, '');

const sanitizeDateOnlyDisplayInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);

    return [day, month, year].filter(Boolean).join('/');
};

const parseDateOnlyFromDisplay = (value: string) => {
    if (!value) {
        return '';
    }

    const [day, month, year] = value.split('/');
    return `${year}-${month}-${day}`;
};

const formatDateOnlyForDisplay = (value?: string) => {
    if (!value) {
        return '';
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

const isValidDateOnlyDisplay = (value: string) => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return false;
    }

    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
    );
};

const hasCollaboratorChanges = (
    collaborator: any,
    payload: {
        name: string;
        dni: string;
        mobilePhone: string;
        email: string;
        empresa: string;
        address: string;
        localidad: string;
        legajo: string;
        birthDate: string;
        jobTitle: string;
        customFields: Record<string, string>;
    },
) => {
    return (
        normalizeComparableText(collaborator?.name) !== normalizeComparableText(payload.name) ||
        normalizeComparableText(collaborator?.dni) !== normalizeComparableText(payload.dni) ||
        normalizePhoneForInput(collaborator?.mobilePhone) !== normalizePhoneForInput(payload.mobilePhone) ||
        normalizeComparableText(collaborator?.email) !== normalizeComparableText(payload.email) ||
        normalizeComparableText(collaborator?.empresa) !== normalizeComparableText(payload.empresa) ||
        normalizeComparableText(collaborator?.address) !== normalizeComparableText(payload.address) ||
        normalizeComparableText(collaborator?.localidad) !== normalizeComparableText(payload.localidad) ||
        normalizeComparableText(collaborator?.legajo) !== normalizeComparableText(payload.legajo) ||
        normalizeComparableText(collaborator?.birthDate) !== normalizeComparableText(payload.birthDate) ||
        normalizeComparableText(collaborator?.jobTitle) !== normalizeComparableText(payload.jobTitle) ||
        !areCustomFieldsEqual(collaborator?.customFields, payload.customFields)
    );
};

const getChangedCustomFieldValues = (
    original: Record<string, string> | undefined,
    next: Record<string, string>,
) => {
    const originalValues = original ?? {};
    const changedValues: Record<string, string> = {};

    Object.keys(next).forEach((fieldID) => {
        if (normalizeComparableText(originalValues[fieldID]) !== normalizeComparableText(next[fieldID])) {
            changedValues[fieldID] = next[fieldID];
        }
    });

    return changedValues;
};

const buildChangedFields = (
    collaborator: any,
    payload: {
        name: string;
        dni: string;
        mobilePhone: string;
        email: string;
        empresa: string;
        address: string;
        localidad: string;
        legajo: string;
        birthDate: string;
        jobTitle: string;
        customFields: Record<string, string>;
    },
) => {
    const changedFields: Record<string, unknown> = {};
    const textFieldKeys = [
        'name', 'dni', 'email', 'empresa', 'address', 'localidad', 'legajo', 'birthDate', 'jobTitle',
    ] as const;

    textFieldKeys.forEach((key) => {
        if (normalizeComparableText(collaborator?.[key]) !== normalizeComparableText(payload[key])) {
            changedFields[key] = payload[key];
        }
    });

    if (normalizePhoneForInput(collaborator?.mobilePhone) !== normalizePhoneForInput(payload.mobilePhone)) {
        changedFields.mobilePhone = payload.mobilePhone;
    }

    const changedCustomFields = getChangedCustomFieldValues(collaborator?.customFields, payload.customFields);
    if (Object.keys(changedCustomFields).length > 0) {
        changedFields.customFields = changedCustomFields;
    }

    return changedFields;
};

const buildCustomFieldFormValues = (
    fields: CollaboratorCustomField[],
    rawValues: Record<string, string>,
) => {
    return fields.reduce<Record<string, string>>((nextValues, field) => {
        const value = rawValues[field.id] ?? '';
        nextValues[field.id] = field.typeId === 'date'
            ? formatDateOnlyForDisplay(value)
            : value;
        return nextValues;
    }, { ...rawValues });
};

const buildCustomFieldsPayload = (
    fields: CollaboratorCustomField[],
    formValues: Record<string, string>,
) => {
    return Object.keys(formValues).reduce<Record<string, string>>((nextValues, fieldID) => {
        const field = fields.find((currentField) => currentField.id === fieldID);
        const value = formValues[fieldID] ?? '';
        nextValues[fieldID] = field?.typeId === 'date'
            ? parseDateOnlyFromDisplay(value)
            : value;
        return nextValues;
    }, {});
};

const areCustomFieldsEqual = (
    left: Record<string, string> | undefined,
    right: Record<string, string> | undefined,
) => {
    const leftValues = left ?? {};
    const rightValues = right ?? {};
    const keys = Array.from(new Set([...Object.keys(leftValues), ...Object.keys(rightValues)]));

    return keys.every((key) =>
        normalizeComparableText(leftValues[key]) === normalizeComparableText(rightValues[key])
    );
};

const formatPhoneForDisplay = (value: string | undefined) => {
    const normalized = normalizePhoneForInput(value);
    return normalized ? `+${normalized}` : 'N/D';
};

const buildInitials = (name: string) => {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!parts.length) {
        return 'C';
    }

    return (parts.length === 1
        ? parts[0][0]
        : `${parts[0][0]}${parts[parts.length - 1][0]}`).toUpperCase();
};

const buildCustomFieldLabel = (field: CollaboratorCustomField) =>
    field.isRequired ? `${field.name} *` : field.name;

const validateCustomFields = (
    fields: CollaboratorCustomField[],
    values: Record<string, string>,
) => {
    return fields.reduce<Record<string, string>>((nextErrors, field) => {
        const value = values[field.id] ?? '';

        if (field.isRequired && field.typeId !== 'boolean' && !value.trim()) {
            nextErrors[field.id] = 'Campo obligatorio'.localized();
            return nextErrors;
        }

        if (field.typeId === 'number' && value && Number.isNaN(Number(value))) {
            nextErrors[field.id] = 'Ingresá un número válido'.localized();
        }

        if (field.typeId === 'date' && value && !isValidDateOnlyDisplay(value)) {
            nextErrors[field.id] = 'Ingresá una fecha válida'.localized();
        }

        return nextErrors;
    }, {});
};

const getCustomFieldDisplayValue = (field: CollaboratorCustomField, value: string) => {
    if (field.typeId === 'boolean') {
        if (!value) {
            return '';
        }

        return value === 'true' ? 'Sí'.localized() : 'No'.localized();
    }

    if (field.typeId === 'list') {
        return field.values?.find((listValue) => listValue.id === value)?.label ?? value;
    }

    return value;
};

const buildListFieldOptions = (field: CollaboratorCustomField): ComboBoxOption[] => {
    return (field.values || []).map((value) => ({
        id: value.id,
        title: value.label,
    }));
};

const sanitizeNumberInput = (value: string) =>
    value.replace(/[^0-9.,-]/g, '').replace(',', '.');


const validateDraft = (
    draft: {
        name: string;
        dni: string;
        mobilePhone: string;
        email: string;
        empresa: string;
        address: string;
        localidad: string;
        legajo: string;
        birthDate: string;
        jobTitle: string;
    },
    roster: Array<{ id: string; name: string; mobilePhone: string; deactivated: boolean }> = [],
    currentCollaboratorID?: string,
) => {
    const nextErrors = {
        name: '',
        dni: '',
        mobilePhone: '',
        email: '',
        empresa: '',
        address: '',
        localidad: '',
        legajo: '',
        birthDate: '',
        jobTitle: '',
    };

    if (draft.name.trim() === '') {
        nextErrors.name = 'Nombre es requerido';
    } else if (draft.name.length > 40) {
        nextErrors.name = 'Nombre no debe ser mayor a 40 caracteres';
    }

    if (draft.mobilePhone.trim() === '') {
        nextErrors.mobilePhone = 'Telefono es requerido';
    } else if (!/\d/.test(draft.mobilePhone)) {
        nextErrors.mobilePhone = 'Teléfono no es válido';
    } else {
        const normalizedPhone = normalizePhoneForComparison(draft.mobilePhone);
        const phoneOwner = roster.find((row) => (
            row.id !== currentCollaboratorID
            && normalizePhoneForComparison(row.mobilePhone) === normalizedPhone
        ));

        if (phoneOwner) {
            const ownerName = phoneOwner.name || 'otro colaborador';
            nextErrors.mobilePhone = phoneOwner.deactivated
                ? 'Ese número de celular está siendo utilizado por %s, que se encuentra en colaboradores desactivados.'.localized(ownerName)
                : 'Ese número de celular está siendo utilizado por %s.'.localized(ownerName);
        }
    }

    if (draft.email.trim() && (!draft.email.includes('@') || !draft.email.includes('.'))) {
        nextErrors.email = 'Email no es válido';
    }

    if (draft.dni.includes('.') || draft.dni.includes(',')) {
        nextErrors.dni = 'DNI no debe contener puntos ni comas';
    }

    if (draft.birthDate && !isValidDateOnlyDisplay(draft.birthDate)) {
        nextErrors.birthDate = 'Fecha inválida. Usá DD/MM/AAAA';
    }

    return nextErrors;
};
